import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import toast from 'react-hot-toast';
import { FaMapMarkerAlt, FaClock, FaUsers, FaCheck, FaTimes, FaComments, FaEdit, FaRupeeSign, FaMoneyBillWave, FaBell } from 'react-icons/fa';
import ChatWindow from '../components/ChatWindow';
import { cleanAddress } from '../utils/addressUtils';
import usePushRefresh from '../hooks/usePushRefresh';

const MyTrips = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [openChat, setOpenChat] = useState(null);
  const [editingTrip, setEditingTrip] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [confirmingCash, setConfirmingCash] = useState(null);
  const [showCashModal, setShowCashModal] = useState(null);
  const [cashAmount, setCashAmount] = useState('');
  const [seenBookings, setSeenBookings] = useState(new Set());
  const [otpInput, setOtpInput] = useState({}); // { tripId: otp }
  // OTP is not stored in state - it's only visible to passengers, not riders
  const bookingRefs = useRef({});
  const locationTrackingIntervals = useRef({});
  const { user } = useAuth();

  const fetchTrips = useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await api.get('/api/trips/my-trips', { params });
      console.log('Fetched trips:', response.data);
      // Ensure trips is always an array
      const tripsData = response.data?.trips || response.data || [];
      setTrips(Array.isArray(tripsData) ? tripsData : []);
      
      // Mark bookings as seen when they come into view
      setTimeout(() => {
        markVisibleBookingsAsSeen(Array.isArray(tripsData) ? tripsData : [], seenBookings);
      }, 100);
    } catch (error) {
      console.error('Fetch trips error:', error);
      toast.error('Failed to load trips');
      setTrips([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, [statusFilter, seenBookings]);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  usePushRefresh(() => {
    fetchTrips();
  });

  // Hide "powered by Google" text elements
  useEffect(() => {
    const hidePoweredByGoogle = () => {
      const walker = document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );
      
      let node;
      while (node = walker.nextNode()) {
        const text = node.textContent || '';
        if (/powered\s+by\s+google/gi.test(text)) {
          let parent = node.parentElement;
          // Hide the parent element containing the text
          if (parent && !parent.classList.contains('hide-powered-by-google')) {
            parent.style.display = 'none';
            parent.style.visibility = 'hidden';
            parent.style.opacity = '0';
            parent.style.height = '0';
            parent.style.width = '0';
            parent.style.overflow = 'hidden';
            parent.classList.add('hide-powered-by-google');
          }
        }
      }
    };
    
    // Run immediately
    hidePoweredByGoogle();
    
    // Run after a short delay to catch dynamically added content
    const timeout1 = setTimeout(hidePoweredByGoogle, 100);
    const timeout2 = setTimeout(hidePoweredByGoogle, 500);
    const timeout3 = setTimeout(hidePoweredByGoogle, 1000);
    
    // Use MutationObserver to catch dynamically added content
    const observer = new MutationObserver(hidePoweredByGoogle);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });
    
    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      observer.disconnect();
    };
  }, [trips]); // Re-run when trips change

  // Real-time polling for trip updates
  useEffect(() => {
    if (!user || user.role !== 'rider') return;

    const refreshIfVisible = () => {
      if (!document.hidden) {
        fetchTrips();
      }
    };

    document.addEventListener('visibilitychange', refreshIfVisible);

    const pollInterval = setInterval(refreshIfVisible, 30000); // 30 seconds

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [user, fetchTrips]);
  

  const markVisibleBookingsAsSeen = async (tripsData, currentSeenBookings = seenBookings) => {
    if (!Array.isArray(tripsData)) return;
    
    const bookingsToMark = [];
    
    tripsData.forEach(trip => {
      if (trip && Array.isArray(trip.bookings)) {
        trip.bookings.forEach(booking => {
          if (booking && booking.status === 'pending' && !booking.seenByRider && !currentSeenBookings.has(booking._id)) {
            bookingsToMark.push(booking._id);
          }
        });
      }
    });

    // Mark all unseen pending bookings as seen
    for (const bookingId of bookingsToMark) {
      try {
        await api.put(`/api/bookings/${bookingId}/mark-seen`);
        setSeenBookings(prev => new Set([...prev, bookingId]));
        
        // Add fade-out animation
        if (bookingRefs.current[bookingId]) {
          bookingRefs.current[bookingId].classList.add('booking-seen');
        }
      } catch (error) {
        console.error('Error marking booking as seen:', error);
      }
    }
  };

  const handleBookingInteraction = async (bookingId) => {
    // Mark as seen when rider interacts with booking
    if (!seenBookings.has(bookingId)) {
      try {
        await api.put(`/api/bookings/${bookingId}/mark-seen`);
        setSeenBookings(prev => new Set([...prev, bookingId]));
        
        if (bookingRefs.current[bookingId]) {
          bookingRefs.current[bookingId].classList.add('booking-seen');
        }
      } catch (error) {
        console.error('Error marking booking as seen:', error);
      }
    }
  };

  const handleGenerateOTP = async (tripId) => {
    try {
      // Get current location first
      if (!navigator.geolocation) {
        toast.error('Location services are required to generate OTP. Please enable location services.');
        return;
      }

      const loadingToast = toast.loading('Checking your location...');
      
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          
          try {
            toast.dismiss(loadingToast);
            const generatingToast = toast.loading('Generating OTP...');
            
            const response = await api.post(`/api/trips/${tripId}/generate-otp`, {
              currentLat: location.lat,
              currentLng: location.lng
            });
            
            toast.dismiss(generatingToast);
            toast.success(response.data.message || 'OTP generated successfully and sent to passengers. Ask your passenger for the OTP.', {
              duration: 5000,
              icon: '✅'
            });
            
            // Immediately refresh trips to show OTP input section
            await fetchTrips();
          } catch (error) {
            toast.dismiss(loadingToast);
            toast.dismiss();
            console.error('Generate OTP error:', error);
            toast.error(error.response?.data?.message || 'Failed to generate OTP');
          }
        },
        (error) => {
          toast.dismiss(loadingToast);
          console.error('Error getting location:', error);
          toast.error('Failed to get your location. Please enable location services and try again.');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } catch (error) {
      console.error('Generate OTP error:', error);
      toast.error(error.response?.data?.message || 'Failed to generate OTP');
    }
  };

  const handleVerifyOTP = async (tripId) => {
    const otp = otpInput[tripId];
    if (!otp || otp.length < 4 || otp.length > 6) {
      toast.error('Please enter a valid OTP (4-6 digits)');
      return;
    }
    
    // Ensure OTP contains only numbers
    if (!/^\d+$/.test(otp)) {
      toast.error('OTP must contain only numbers');
      return;
    }

    try {
      // Optimistic update - clear OTP input immediately
      setOtpInput(prev => {
        const newState = { ...prev };
        delete newState[tripId];
        return newState;
      });
      // OTP is not stored in state anymore - it's only in booking data (not visible to rider)
      
      const loadingToast = toast.loading('Verifying OTP and starting trip...');
      
      // Get current location before verifying OTP
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            
            try {
              // Verify OTP and start trip
              await api.post(`/api/trips/${tripId}/verify-otp`, { otp });
              
              // Update rider location (non-blocking)
              api.put(`/api/trips/${tripId}/rider-location`, location).catch(err => {
                console.warn('Failed to update initial location:', err);
              });
              
              toast.dismiss(loadingToast);
              toast.success('✅ OTP verified! Trip started successfully. You are now being tracked.', {
                duration: 5000,
                icon: '🚀'
              });
              
              // Refresh in background
              fetchTrips().catch(err => console.error('Background refresh error:', err));
              
              // Start continuous location updates for this trip
              startLocationTracking(tripId);
            } catch (error) {
              toast.dismiss(loadingToast);
              toast.error(error.response?.data?.message || 'Failed to verify OTP');
              // Restore OTP input on error
              setOtpInput(prev => ({ ...prev, [tripId]: otp }));
            }
          },
          async (error) => {
            console.error('Error getting location:', error);
            try {
              // Verify OTP anyway
              await api.post(`/api/trips/${tripId}/verify-otp`, { otp });
              toast.dismiss(loadingToast);
              toast.success('✅ OTP verified! Trip started (location tracking unavailable)', {
                duration: 5000
              });
              fetchTrips().catch(err => console.error('Background refresh error:', err));
            } catch (verifyError) {
              toast.dismiss(loadingToast);
              toast.error(verifyError.response?.data?.message || 'Failed to verify OTP');
              // Restore OTP input on error
              setOtpInput(prev => ({ ...prev, [tripId]: otp }));
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        );
      } else {
        // No geolocation support, just verify OTP
        try {
          await api.post(`/api/trips/${tripId}/verify-otp`, { otp });
          toast.dismiss(loadingToast);
          toast.success('✅ OTP verified! Trip started successfully', {
            duration: 5000
          });
          fetchTrips().catch(err => console.error('Background refresh error:', err));
        } catch (error) {
          toast.dismiss(loadingToast);
          toast.error(error.response?.data?.message || 'Failed to verify OTP');
          // Restore OTP input on error
          setOtpInput(prev => ({ ...prev, [tripId]: otp }));
        }
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      toast.error(error.response?.data?.message || 'Invalid OTP. Please try again.');
    }
  };

  // Track location for active trips
  const startLocationTracking = (tripId) => {
    // Stop any existing tracking for this trip
    if (locationTrackingIntervals.current[tripId]) {
      clearInterval(locationTrackingIntervals.current[tripId]);
    }

    // Start location tracking every 10 seconds
    locationTrackingIntervals.current[tripId] = setInterval(() => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            
            try {
              await api.put(`/api/trips/${tripId}/rider-location`, location);
            } catch (error) {
              // If trip is no longer active, stop tracking
              if (error.response?.status === 400 || error.response?.status === 404) {
                stopLocationTracking(tripId);
              }
            }
          },
          (error) => {
            console.error('Error updating location:', error);
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
          }
        );
      }
    }, 10000); // Update every 10 seconds
  };

  const stopLocationTracking = (tripId) => {
    if (locationTrackingIntervals.current[tripId]) {
      clearInterval(locationTrackingIntervals.current[tripId]);
      delete locationTrackingIntervals.current[tripId];
    }
  };

  // Cleanup location tracking on unmount
  useEffect(() => {
    return () => {
      Object.values(locationTrackingIntervals.current).forEach(interval => {
        clearInterval(interval);
      });
    };
  }, []);

  // Start/stop location tracking based on trip status
  useEffect(() => {
    if (!user || user.role !== 'rider') return;

    trips.forEach(trip => {
      if (trip.status === 'in_progress' || trip.status === 'booked') {
        // Start tracking if not already tracking
        if (!locationTrackingIntervals.current[trip._id]) {
          startLocationTracking(trip._id);
        }
      } else {
        // Stop tracking if trip is not active
        stopLocationTracking(trip._id);
      }
    });

    return () => {
      // Cleanup will be handled by the main cleanup effect
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trips, user]);

  const handleCompleteTrip = async (tripId) => {
    try {
      const loadingToast = toast.loading('Completing trip...');
      
      // Get current location to validate proximity to destination
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const location = {
              currentLat: position.coords.latitude,
              currentLng: position.coords.longitude
            };
            
            try {
              await api.post(`/api/trips/${tripId}/complete`, location);
              
              toast.dismiss(loadingToast);
              toast.success('✅ Trip completed successfully! All passengers have been notified.', {
                duration: 5000,
                icon: '🎉'
              });
              
              // Stop location tracking
              stopLocationTracking(tripId);
              
              // Refresh in background
              fetchTrips().catch(err => console.error('Background refresh error:', err));
            } catch (error) {
              console.error('Complete trip error:', error);
              toast.dismiss(loadingToast);
              const errorMessage = error.response?.data?.message || 'Failed to complete trip';
              toast.error(errorMessage);
            }
          },
          async (error) => {
            console.error('Error getting location:', error);
            // Try without location (will use stored location if available)
            try {
              await api.post(`/api/trips/${tripId}/complete`);
              toast.success('Trip completed successfully');
              await fetchTrips();
              stopLocationTracking(tripId);
            } catch (completeError) {
              console.error('Complete trip error:', completeError);
              toast.error(completeError.response?.data?.message || 'Failed to complete trip');
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        );
      } else {
        // No geolocation support, try without location
        await api.post(`/api/trips/${tripId}/complete`);
        toast.success('Trip completed successfully');
        await fetchTrips();
        stopLocationTracking(tripId);
      }
    } catch (error) {
      console.error('Complete trip error:', error);
      toast.error(error.response?.data?.message || 'Failed to complete trip');
    }
  };

  const handleAcceptBooking = async (bookingId) => {
    try {
      await api.put(`/api/bookings/${bookingId}/accept`);
      toast.success('Booking accepted');
      // Immediate refresh
      await fetchTrips();
    } catch (error) {
      console.error('Accept booking error:', error);
      toast.error(error.response?.data?.message || 'Failed to accept booking');
    }
  };

  const handleRejectBooking = async (bookingId) => {
    try {
      const loadingToast = toast.loading('Rejecting booking...');
      
      await api.put(`/api/bookings/${bookingId}/reject`);
      
      toast.dismiss(loadingToast);
      toast.success('Booking rejected', {
        duration: 3000
      });
      
      fetchTrips().catch(err => console.error('Background refresh error:', err));
    } catch (error) {
      console.error('Reject booking error:', error);
      toast.error(error.response?.data?.message || 'Failed to reject booking');
    }
  };

  const handleCancelTrip = async (tripId) => {
    if (!window.confirm('Are you sure you want to cancel this trip?')) return;

    try {
      await api.delete(`/api/trips/${tripId}`);
      toast.success('Trip cancelled successfully');
      // Immediate refresh
      await fetchTrips();
    } catch (error) {
      console.error('Cancel trip error:', error);
      toast.error('Failed to cancel trip');
    }
  };

  const handleEditPrice = (trip) => {
    setEditingTrip(trip._id);
    setEditPrice(trip.price.toString());
  };

  const handleCancelEdit = () => {
    setEditingTrip(null);
    setEditPrice('');
  };

  const handleSavePrice = async (tripId) => {
    const newPrice = parseFloat(editPrice);
    if (!newPrice || newPrice <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    setEditLoading(true);
    try {
      await api.put(`/api/trips/${tripId}`, { price: newPrice });
      toast.success('Trip price updated successfully');
      setEditingTrip(null);
      setEditPrice('');
      fetchTrips();
    } catch (error) {
      console.error('Update price error:', error);
      const message = error.response?.data?.message || 'Failed to update price';
      toast.error(message);
    } finally {
      setEditLoading(false);
    }
  };

  const handleConfirmCashPayment = async () => {
    if (!showCashModal) return;
    
    const amount = parseFloat(cashAmount);
    const expectedAmount = showCashModal.amount;
    
    if (!cashAmount || isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setConfirmingCash(showCashModal._id);
    try {
      await api.post(`/api/payments/confirm-cash/${showCashModal._id}`);
      toast.success(`Cash payment of ₹${amount} confirmed successfully`);
      setShowCashModal(null);
      setCashAmount('');
      // Immediate refresh
      await fetchTrips();
    } catch (error) {
      console.error('Confirm cash payment error:', error);
      toast.error(error.response?.data?.message || 'Failed to confirm cash payment');
    } finally {
      setConfirmingCash(null);
    }
  };

  const openCashModal = (booking) => {
    setShowCashModal(booking);
    setCashAmount(booking.amount.toString());
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      open: 'badge-info',
      booked: 'badge-warning',
      in_progress: 'badge-success',
      completed: 'badge-success',
      cancelled: 'badge-danger'
    };
    
    return (
      <span className={`badge ${statusClasses[status] || 'badge-secondary'}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">My Trips</h1>
            <a href="/create-trip" className="btn-primary w-full sm:w-auto">
              Create New Trip
            </a>
          </div>

          {/* Filter */}
          <div className="mb-6">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field w-auto"
            >
              <option value="all">All Trips</option>
              <option value="open">Open</option>
              <option value="booked">Booked</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Trips List */}
          {trips && Array.isArray(trips) && trips.length > 0 ? (
            <div className="space-y-4">
              {trips.map((trip) => (
                <div key={trip._id} className="card">
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                    <div className="flex-1 w-full">
                      {/* Route */}
                      <div className="mb-4">
                        <div className="flex items-start mb-2">
                          <FaMapMarkerAlt className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                          <span className="font-medium text-sm sm:text-base break-words">{cleanAddress(trip.origin.address)}</span>
                        </div>
                        <div className="flex items-start">
                          <FaMapMarkerAlt className="text-red-500 mr-2 mt-1 flex-shrink-0" />
                          <span className="font-medium text-sm sm:text-base break-words">{cleanAddress(trip.destination.address)}</span>
                        </div>
                      </div>

                      {/* Trip Details */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-4">
                        <div className="flex items-center text-gray-600 text-sm sm:text-base">
                          <FaClock className="mr-2 flex-shrink-0" />
                          <span className="break-words">{new Date(trip.departureTime).toLocaleString()}</span>
                        </div>
                        <div className="flex items-center text-gray-600 text-sm sm:text-base">
                          <FaUsers className="mr-2 flex-shrink-0" />
                          <span>{trip.bookedSeats}/{trip.availableSeats} seats</span>
                        </div>
                        <div className="text-gray-600">
                          {editingTrip === trip._id ? (
                            <div className="flex items-center gap-2">
                              <FaRupeeSign className="text-gray-600" />
                              <input
                                type="number"
                                value={editPrice}
                                onChange={(e) => setEditPrice(e.target.value)}
                                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                min="1"
                                step="0.01"
                                autoFocus
                              />
                              <button
                                onClick={() => handleSavePrice(trip._id)}
                                disabled={editLoading}
                                className="px-2 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                              >
                                {editLoading ? '...' : 'Save'}
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                disabled={editLoading}
                                className="px-2 py-1 bg-gray-400 text-white rounded text-sm hover:bg-gray-500 disabled:opacity-50"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">₹{trip.price}</span>
                              <span>per seat</span>
                              {trip.status === 'open' && (
                                <button
                                  onClick={() => handleEditPrice(trip)}
                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                  title="Edit price"
                                >
                                  <FaEdit />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Status */}
                      <div className="mb-4">
                        {getStatusBadge(trip.status)}
                      </div>

                      {/* Bookings */}
                      {trip.bookings && trip.bookings.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">Passengers</h4>
                          <div className="space-y-2 overflow-x-auto">
                            {trip.bookings.map((booking) => {
                              const isUnseen = booking.status === 'pending' && !booking.seenByRider && !seenBookings.has(booking._id);
                              return (
                                <div 
                                  key={booking._id} 
                                  ref={el => bookingRefs.current[booking._id] = el}
                                  className={`bg-gray-50 p-3 rounded transition-all duration-500 ${
                                    isUnseen 
                                      ? 'ring-2 ring-blue-500 shadow-lg bg-blue-50 animate-pulse' 
                                      : 'booking-seen'
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center flex-1 min-w-0">
                                      <div className="relative">
                                        <img
                                          src={booking.passenger?.photo || '/default-avatar.png'}
                                          alt={booking.passenger?.name}
                                          className="w-8 h-8 rounded-full mr-3 flex-shrink-0"
                                        />
                                        {isUnseen && (
                                          <span className="absolute -top-1 -right-1 bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                                            <FaBell className="text-xs" />
                                          </span>
                                        )}
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <div className="font-medium truncate flex flex-wrap items-center gap-1 text-sm sm:text-base">
                                          <span className="truncate">{booking.passenger?.name}</span>
                                          {isUnseen && (
                                            <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full animate-pulse whitespace-nowrap">
                                              New Request
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-xs sm:text-sm text-gray-600 break-words">
                                          Status: {booking.status} | Payment: {booking.paymentStatus}
                                          {booking.payment?.paymentMethod === 'cash' && ' (Cash)'}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="text-sm sm:text-base text-gray-600 font-semibold ml-2 flex-shrink-0">
                                      ₹{booking.amount}
                                    </div>
                                  </div>
                                {booking.status === 'pending' && (
                                  <div className="mt-2">
                                    <div className="flex flex-wrap gap-2">
                                      <button
                                        onClick={() => {
                                          handleBookingInteraction(booking._id);
                                          handleAcceptBooking(booking._id);
                                        }}
                                        className="flex items-center justify-center space-x-1 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 text-sm min-h-[44px]"
                                      >
                                        <FaCheck />
                                        <span>Accept</span>
                                      </button>
                                      <button
                                        onClick={() => {
                                          handleBookingInteraction(booking._id);
                                          handleRejectBooking(booking._id);
                                        }}
                                        className="flex items-center justify-center space-x-1 px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 text-sm min-h-[44px]"
                                      >
                                        <FaTimes />
                                        <span>Reject</span>
                                      </button>
                                      <button
                                        onClick={() => {
                                          handleBookingInteraction(booking._id);
                                          setOpenChat(booking._id);
                                        }}
                                        className="flex items-center justify-center space-x-1 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 text-sm min-h-[44px]"
                                      >
                                        <FaComments />
                                        <span>Chat</span>
                                      </button>
                                    </div>
                                  </div>
                                )}
                                {(booking.status === 'accepted' || booking.status === 'completed') && (
                                  <div className="mt-2">
                                    <div className="flex flex-wrap gap-2">
                                      {booking.paymentStatus === 'pending' && booking.payment?.paymentMethod === 'cash' && (
                                        <button
                                          onClick={() => openCashModal(booking)}
                                          disabled={confirmingCash === booking._id}
                                          className="flex items-center justify-center space-x-1 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm min-h-[44px]"
                                        >
                                          <FaMoneyBillWave />
                                          <span>{confirmingCash === booking._id ? 'Confirming...' : 'Confirm Cash Payment'}</span>
                                        </button>
                                      )}
                                      {booking.paymentStatus === 'paid' && booking.payment?.paymentMethod === 'cash' && (
                                        <div className="px-3 sm:px-4 py-2 bg-green-50 text-green-700 rounded-lg text-xs sm:text-sm font-semibold">
                                          <FaCheck className="inline mr-1" />
                                          Cash Payment Confirmed
                                        </div>
                                      )}
                                      <button
                                        onClick={() => setOpenChat(booking._id)}
                                        className="flex items-center justify-center space-x-1 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 text-sm min-h-[44px]"
                                      >
                                        <FaComments />
                                        <span>Chat</span>
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="w-full sm:w-auto sm:ml-6 flex flex-col sm:flex-col space-y-2 mt-4 sm:mt-0">
                      {trip.status === 'open' && (
                        <button
                          onClick={() => handleCancelTrip(trip._id)}
                          className="btn-danger text-sm w-full sm:w-auto"
                        >
                          Cancel Trip
                        </button>
                      )}
                      
                      {trip.status === 'booked' && (
                        <div className="space-y-2 w-full sm:w-auto">
                          {(() => {
                            // Check if OTP has been generated (from booking data)
                            // Check all accepted bookings for OTP
                            const acceptedBookings = trip.bookings?.filter(b => b.status === 'accepted') || [];
                            const acceptedBookingWithOTP = acceptedBookings.find(b => b.pickupOTP);
                            const hasOTP = !!(acceptedBookingWithOTP && acceptedBookingWithOTP.pickupOTP);
                            
                            // Debug log for troubleshooting
                            if (trip.status === 'booked' && process.env.NODE_ENV === 'development') {
                              console.log('Trip ID:', trip._id);
                              console.log('All bookings:', trip.bookings);
                              console.log('Accepted bookings:', acceptedBookings);
                              console.log('Booking with OTP:', acceptedBookingWithOTP);
                              console.log('hasOTP:', hasOTP);
                            }
                            
                            return !hasOTP ? (
                              <button
                                onClick={() => handleGenerateOTP(trip._id)}
                                className="btn-success text-sm w-full sm:w-auto"
                              >
                                Generate OTP
                              </button>
                            ) : (
                              <div className="space-y-3 w-full">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                                  <div className="font-semibold text-blue-800 mb-1">OTP Status:</div>
                                  <div className="text-sm text-blue-600 text-center">OTP has been generated and sent to passengers</div>
                                  <div className="text-xs text-blue-500 text-center mt-1">Ask your passenger for the OTP to start the trip</div>
                                </div>
                                
                                {/* OTP Input Section */}
                                <div className="bg-white border-2 border-blue-300 rounded-lg p-4">
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Enter OTP from Passenger
                                  </label>
                                  <div className="flex flex-col sm:flex-row gap-2">
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      pattern="[0-9]*"
                                      placeholder="Enter 6-digit OTP"
                                      value={otpInput[trip._id] || ''}
                                      onChange={(e) => {
                                        // Only allow numbers
                                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                        setOtpInput(prev => ({ ...prev, [trip._id]: value }));
                                      }}
                                      className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-semibold text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      maxLength={6}
                                      autoComplete="off"
                                    />
                                    <button
                                      onClick={() => handleVerifyOTP(trip._id)}
                                      disabled={!otpInput[trip._id] || otpInput[trip._id].length < 4 || otpInput[trip._id].length > 6}
                                      className={`px-6 py-3 rounded-lg font-medium text-sm whitespace-nowrap ${
                                        otpInput[trip._id] && otpInput[trip._id].length >= 4 && otpInput[trip._id].length <= 6
                                          ? 'bg-green-600 hover:bg-green-700 text-white'
                                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                      } transition-colors`}
                                    >
                                      Verify & Start
                                    </button>
                                  </div>
                                  <div className="flex justify-between items-center mt-2">
                                    <button
                                      onClick={() => handleGenerateOTP(trip._id)}
                                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                                    >
                                      Regenerate OTP
                                    </button>
                                    {otpInput[trip._id] && (
                                      <span className="text-xs text-gray-500">
                                        {otpInput[trip._id].length}/6 digits
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      
                      {trip.status === 'in_progress' && (
                        <button
                          onClick={() => handleCompleteTrip(trip._id)}
                          className="btn-primary text-sm w-full sm:w-auto"
                        >
                          Complete Trip
                        </button>
                      )}
                      
                      <a
                        href={`/trip/${trip._id}`}
                        className="btn-secondary text-sm text-center w-full sm:w-auto"
                      >
                        View Details
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FaMapMarkerAlt className="mx-auto text-gray-400 text-6xl mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                No trips found
              </h3>
              <p className="text-gray-500 mb-6">
                {statusFilter === 'all' 
                  ? "You haven't created any trips yet."
                  : `No trips with status "${statusFilter}" found.`
                }
              </p>
              <a href="/create-trip" className="btn-primary">
                Create Your First Trip
              </a>
            </div>
          )}
        </div>
      </div>
      
      {/* Cash Payment Confirmation Modal */}
      {showCashModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center mb-4">
              <FaMoneyBillWave className="text-5xl text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2 text-center">
              Confirm Cash Payment
            </h3>
            <p className="text-gray-600 text-center mb-6">
              Confirm that you have received cash payment from the passenger
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Passenger Name
              </label>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-800">
                  {showCashModal.passenger?.name || 'Unknown'}
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Total Amount Collected
              </label>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-bold text-lg text-gray-800">
                  ₹{showCashModal.amount}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  Includes platform fee (₹{showCashModal.payment?.platformFee || (showCashModal.amount * 0.1 / 1.1).toFixed(2)})
                </p>
              </div>
            </div>

            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-gray-700">Your Earnings:</span>
                <span className="font-bold text-green-700">
                  ₹{showCashModal.payment?.riderAmount || (showCashModal.amount / 1.1).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700">Platform Fee:</span>
                <span className="text-sm text-gray-600">
                  ₹{showCashModal.payment?.platformFee || (showCashModal.amount * 0.1 / 1.1).toFixed(2)}
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-2">
                💡 Only your earnings (₹{showCashModal.payment?.riderAmount || (showCashModal.amount / 1.1).toFixed(2)}) will be credited to your wallet. Platform fee is already collected in cash.
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount Received (₹)
              </label>
              <input
                type="number"
                value={cashAmount}
                onChange={(e) => setCashAmount(e.target.value)}
                placeholder="Enter amount received"
                min="0"
                step="0.01"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                autoFocus
              />
              {cashAmount && parseFloat(cashAmount) !== showCashModal.amount && (
                <p className="text-sm text-yellow-600 mt-2">
                  ⚠️ Amount differs from expected ₹{showCashModal.amount}
                </p>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowCashModal(null);
                  setCashAmount('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmCashPayment}
                disabled={confirmingCash === showCashModal._id || !cashAmount}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {confirmingCash === showCashModal._id ? 'Confirming...' : 'Confirm Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat Window */}
      {openChat && (
        <ChatWindow
          bookingId={openChat}
          onClose={() => setOpenChat(null)}
          currentUser={user}
        />
      )}
      
      {/* Hide Google attribution elements */}
      <style>{`
        /* Hide "powered by Google" attribution from Google Maps/Places */
        .pac-container::after,
        .pac-container::before,
        [class*="powered-by-google"],
        [class*="poweredByGoogle"],
        .gm-style-cc,
        .gm-style-cc > div,
        a[href*="google.com/maps"],
        a[href*="maps.google.com"],
        .hide-powered-by-google {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          height: 0 !important;
          width: 0 !important;
          overflow: hidden !important;
          font-size: 0 !important;
          line-height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
        }
      `}</style>
    </div>
  );
};

export default MyTrips;
